import React from 'react';
import { ApiError } from '../services/api';

// Error types for better categorization
export const ERROR_TYPES = {
  NETWORK: 'NETWORK',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  VALIDATION: 'VALIDATION',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  SERVER: 'SERVER',
  RATE_LIMIT: 'RATE_LIMIT',
  UNKNOWN: 'UNKNOWN'
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Enhanced error handler class
export class ErrorHandler {
  constructor() {
    this.errorListeners = [];
    this.setupGlobalErrorHandlers();
  }

  // Add error listener
  addErrorListener(callback) {
    this.errorListeners.push(callback);
  }

  // Remove error listener
  removeErrorListener(callback) {
    this.errorListeners = this.errorListeners.filter(listener => listener !== callback);
  }

  // Notify all error listeners
  notifyErrorListeners(error) {
    this.errorListeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        console.error('Error in error listener:', err);
      }
    });
  }

  // Setup global error handlers
  setupGlobalErrorHandlers() {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      const error = this.normalizeError(event.reason);
      this.handleError(error);
    });

    // Handle global JavaScript errors
    window.addEventListener('error', (event) => {
      console.error('Global JavaScript error:', event.error);
      const error = this.normalizeError(event.error);
      this.handleError(error);
    });
  }

  // Normalize different error types into a consistent format
  normalizeError(error) {
    if (error instanceof ApiError) {
      return {
        type: this.getErrorType(error.status),
        severity: this.getErrorSeverity(error.status),
        message: error.getUserFriendlyMessage(),
        originalMessage: error.message,
        status: error.status,
        code: error.code,
        details: error.details,
        timestamp: new Date().toISOString(),
        source: 'api'
      };
    }

    if (error instanceof Error) {
      return {
        type: ERROR_TYPES.UNKNOWN,
        severity: ERROR_SEVERITY.MEDIUM,
        message: this.getUserFriendlyMessage(error),
        originalMessage: error.message,
        status: null,
        code: error.code || 'UNKNOWN_ERROR',
        details: null,
        timestamp: new Date().toISOString(),
        source: 'client'
      };
    }

    // Handle string errors or other types
    return {
      type: ERROR_TYPES.UNKNOWN,
      severity: ERROR_SEVERITY.LOW,
      message: typeof error === 'string' ? error : 'An unexpected error occurred',
      originalMessage: error,
      status: null,
      code: 'UNKNOWN_ERROR',
      details: null,
      timestamp: new Date().toISOString(),
      source: 'unknown'
    };
  }

  // Get error type based on status code
  getErrorType(status) {
    if (!status) return ERROR_TYPES.UNKNOWN;

    switch (status) {
      case 400:
      case 422:
        return ERROR_TYPES.VALIDATION;
      case 401:
        return ERROR_TYPES.AUTHENTICATION;
      case 403:
        return ERROR_TYPES.AUTHORIZATION;
      case 404:
        return ERROR_TYPES.NOT_FOUND;
      case 409:
        return ERROR_TYPES.CONFLICT;
      case 429:
        return ERROR_TYPES.RATE_LIMIT;
      case 500:
      case 502:
      case 503:
      case 504:
        return ERROR_TYPES.SERVER;
      default:
        if (status >= 400 && status < 500) {
          return ERROR_TYPES.VALIDATION;
        } else if (status >= 500) {
          return ERROR_TYPES.SERVER;
        }
        return ERROR_TYPES.UNKNOWN;
    }
  }

  // Get error severity based on status code
  getErrorSeverity(status) {
    if (!status) return ERROR_SEVERITY.MEDIUM;

    switch (status) {
      case 400:
      case 404:
      case 422:
        return ERROR_SEVERITY.LOW;
      case 401:
      case 403:
      case 409:
        return ERROR_SEVERITY.MEDIUM;
      case 429:
        return ERROR_SEVERITY.HIGH;
      case 500:
      case 502:
      case 503:
      case 504:
        return ERROR_SEVERITY.CRITICAL;
      default:
        return ERROR_SEVERITY.MEDIUM;
    }
  }

  // Get user-friendly message for generic errors
  getUserFriendlyMessage(error) {
    const message = error.message || 'An unexpected error occurred';

    // Common error patterns
    if (message.includes('Network Error') || message.includes('fetch')) {
      return 'Network connection problem. Please check your internet connection.';
    }
    
    if (message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    
    if (message.includes('CORS')) {
      return 'Access denied due to security policy.';
    }

    if (message.includes('Firebase')) {
      return 'Authentication service error. Please try logging in again.';
    }

    return message;
  }

  // Main error handling method
  handleError(error, context = {}) {
    const normalizedError = this.normalizeError(error);
    
    // Add context information
    const enrichedError = {
      ...normalizedError,
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ...context
      }
    };

    // Log error based on severity
    this.logError(enrichedError);

    // Notify error listeners
    this.notifyErrorListeners(enrichedError);

    // Handle specific error types
    this.handleSpecificErrorTypes(enrichedError);

    return enrichedError;
  }

  // Log error based on severity
  logError(error) {
    const logMessage = `[${error.severity.toUpperCase()}] ${error.type}: ${error.message}`;
    
    switch (error.severity) {
      case ERROR_SEVERITY.LOW:
        console.warn(logMessage, error);
        break;
      case ERROR_SEVERITY.MEDIUM:
        console.error(logMessage, error);
        break;
      case ERROR_SEVERITY.HIGH:
      case ERROR_SEVERITY.CRITICAL:
        console.error(logMessage, error);
        // In production, you might want to send to error tracking service
        this.reportError(error);
        break;
      default:
        console.error(logMessage, error);
    }
  }

  // Handle specific error types
  handleSpecificErrorTypes(error) {
    switch (error.type) {
      case ERROR_TYPES.AUTHENTICATION:
        this.handleAuthenticationError(error);
        break;
      case ERROR_TYPES.AUTHORIZATION:
        this.handleAuthorizationError(error);
        break;
      case ERROR_TYPES.NETWORK:
        this.handleNetworkError(error);
        break;
      case ERROR_TYPES.RATE_LIMIT:
        this.handleRateLimitError(error);
        break;
      default:
        // Default handling is already done in logError
        break;
    }
  }

  // Handle authentication errors
  handleAuthenticationError(error) {
    // Clear auth state and redirect to login
    localStorage.removeItem('user');
    localStorage.removeItem('company');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    
    // Redirect to login page if not already there
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }

  // Handle authorization errors
  handleAuthorizationError(error) {
    // Show permission denied message
    console.warn('Permission denied:', error.message);
  }

  // Handle network errors
  handleNetworkError(error) {
    // You might want to show an offline indicator or retry mechanism
    console.warn('Network error detected:', error.message);
  }

  // Handle rate limit errors
  handleRateLimitError(error) {
    // Show rate limit message and suggest retry after delay
    console.warn('Rate limit exceeded:', error.message);
  }

  // Report error to external service (placeholder)
  reportError(error) {
    // In production, send to error tracking service like Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error);
      console.log('Would report error to tracking service:', error);
    }
  }

  // Create toast notification helper
  createToastNotification(error) {
    return {
      type: this.getToastType(error.severity),
      title: this.getToastTitle(error.type),
      message: error.message,
      duration: this.getToastDuration(error.severity),
      actions: this.getToastActions(error.type)
    };
  }

  // Get toast type based on severity
  getToastType(severity) {
    switch (severity) {
      case ERROR_SEVERITY.LOW:
        return 'info';
      case ERROR_SEVERITY.MEDIUM:
        return 'warning';
      case ERROR_SEVERITY.HIGH:
      case ERROR_SEVERITY.CRITICAL:
        return 'error';
      default:
        return 'error';
    }
  }

  // Get toast title based on error type
  getToastTitle(type) {
    switch (type) {
      case ERROR_TYPES.AUTHENTICATION:
        return 'Authentication Error';
      case ERROR_TYPES.AUTHORIZATION:
        return 'Permission Denied';
      case ERROR_TYPES.VALIDATION:
        return 'Invalid Input';
      case ERROR_TYPES.NOT_FOUND:
        return 'Not Found';
      case ERROR_TYPES.CONFLICT:
        return 'Conflict';
      case ERROR_TYPES.SERVER:
        return 'Server Error';
      case ERROR_TYPES.RATE_LIMIT:
        return 'Rate Limit Exceeded';
      case ERROR_TYPES.NETWORK:
        return 'Network Error';
      default:
        return 'Error';
    }
  }

  // Get toast duration based on severity
  getToastDuration(severity) {
    switch (severity) {
      case ERROR_SEVERITY.LOW:
        return 3000; // 3 seconds
      case ERROR_SEVERITY.MEDIUM:
        return 5000; // 5 seconds
      case ERROR_SEVERITY.HIGH:
        return 8000; // 8 seconds
      case ERROR_SEVERITY.CRITICAL:
        return 0; // Manual dismiss
      default:
        return 5000;
    }
  }

  // Get toast actions based on error type
  getToastActions(type) {
    switch (type) {
      case ERROR_TYPES.AUTHENTICATION:
        return [
          { label: 'Login', action: () => window.location.href = '/login' }
        ];
      case ERROR_TYPES.NETWORK:
        return [
          { label: 'Retry', action: () => window.location.reload() }
        ];
      default:
        return [];
    }
  }
}

// Create global error handler instance
export const errorHandler = new ErrorHandler();

// Convenience function for handling async operations
export const handleAsyncError = async (asyncFn, context = {}) => {
  try {
    return await asyncFn();
  } catch (error) {
    const handledError = errorHandler.handleError(error, context);
    throw handledError;
  }
};

// Higher-order component for error boundary
export const withErrorHandling = (Component) => {
  return class extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
      errorHandler.handleError(error, { errorInfo });
    }

    render() {
      if (this.state.hasError) {
        return (
          <div className="error-fallback">
            <h2>Something went wrong</h2>
            <p>We're sorry, but something went wrong. Please try refreshing the page.</p>
            <button onClick={() => window.location.reload()}>Refresh Page</button>
          </div>
        );
      }

      return <Component {...this.props} />;
    }
  };
};

// Custom hook for error handling
export const useErrorHandler = () => {
  const handleError = (error, context = {}) => {
    return errorHandler.handleError(error, context);
  };

  const handleAsyncOperation = async (asyncFn, context = {}) => {
    return handleAsyncError(asyncFn, context);
  };

  return {
    handleError,
    handleAsyncOperation,
    createToastNotification: (error) => errorHandler.createToastNotification(error)
  };
};

export default errorHandler;